import { FC, useState, useCallback } from 'react';
import { ProjectList } from './components/ProjectList/ProjectList';
import { Notification } from './components/Notification/Notification';
import { useProjects } from './hooks/useProjects';
import { ProjectCreateInput } from './types/project';
import './styles/global.css';

interface NotificationState {
  message: string;
  type: 'success' | 'error';
}

export const App: FC = () => {
  const { projects, loading, error, createProject, updateProject, deleteProject } = useProjects();
  const [notification, setNotification] = useState<NotificationState | null>(null);

  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
  }, []);

  const handleCreateProject = useCallback(async (projectData: ProjectCreateInput) => {
    try {
      await createProject(projectData);
      showNotification('案件を作成しました', 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '案件の作成に失敗しました',
        'error'
      );
      throw error;
    }
  }, [createProject, showNotification]);

  const handleUpdateProject = useCallback(async (id: string, projectData: Partial<ProjectCreateInput>) => {
    try {
      await updateProject(id, projectData);
      showNotification('案件を更新しました', 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '案件の更新に失敗しました',
        'error'
      );
      throw error;
    }
  }, [updateProject, showNotification]);

  const handleDeleteProject = useCallback(async (id: string) => {
    try {
      await deleteProject(id);
      showNotification('案件を削除しました', 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '案件の削除に失敗しました',
        'error'
      );
      throw error;
    }
  }, [deleteProject, showNotification]);

  if (loading) {
    return <div className="container">読み込み中...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div style={{ color: '#f44336' }}>エラー: {error}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <header>
        <h1>案件進捗・炎上管理</h1>
      </header>

      <main>
        <ProjectList
          projects={projects}
          onCreateProject={handleCreateProject}
          onUpdateProject={handleUpdateProject}
          onDeleteProject={handleDeleteProject}
        />
      </main>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

App.displayName = 'App';